import { PageLoader, Toast } from "@asurion-core/mx-asurion-ui-react";
import { LoginPage, shouldForwardProp, useAUIThemeClassName } from "@ubif/components";
import { LayoutFeature, SignInErrorCode } from "@ubif/types";
import {
  fullStory,
  getVersionInfo,
  handleTrainingDue,
  initBugsnag,
  removeToast,
  selectDueTraining,
  selectIsAuthenticated,
  selectLayoutFeature,
  selectSBApiError,
  selectSessionExpiry,
  selectToasts,
  showToast,
  useBugsnag,
  useGetUserQuery,
  useGlobalDispatch,
  useLoadFeatures,
  useTypedSelector
} from "@ubif/util-store";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { registerApplication } from "single-spa";
import { constructApplications, constructRoutes } from "single-spa-layout";
import styled, { StyleSheetManager } from "styled-components";

import { ConnectedPage } from "./ConnectedPage";
import { SBApiErrorsModal } from "./SBApiErrorsModal";
import { ReleaseNotesPage } from "./components/ReleaseNotes/ReleaseNotesPage";
import { UserPinModal } from "./components/UserPinModal";
import microfrontendLayout from "./microfrontend-layout.html";

/** This is used to initialize sub-apps */
const routes = constructRoutes(microfrontendLayout);
const applications = constructApplications({
  routes,
  loadApp({ name }) {
    return System.import(name);
  }
});
type OnceFn<I, R> = (...args: I[]) => R;
function once<I, R>(fn: OnceFn<I, R>): typeof fn {
  let called = false;
  return (...args) => {
    if (called) return;
    called = true;
    return fn(...args);
  };
}
/** We need to register apps but only once */
const registerApps = once(() => applications.forEach(registerApplication));

/** Adds support chat script */
let chatAdded = false;
let chatLoaded = false;
const addAmazonConnectChat = () => {
  if (process.env.SUPPORT_CHAT_ENABLED !== "true") return;
  if (chatAdded) return;
  chatAdded = true;

  // fetch widget config from API before loading script
  fetch("/api/connect/widget-config", {
    credentials: "include" // include cookies for auth
  })
    .then((res) => {
      if (!res.ok) {
        throw new Error(`Config fetch failed: ${res.status}`);
      }
      return res.json();
    })
    .then((config: Window["__UBIF_CONNECT_CONFIG__"]) => {
      // store config in global window variable for widget script to consume
      window.__UBIF_CONNECT_CONFIG__ = config;

      // add script to document
      const { hash } = getVersionInfo();
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.id = "amazon-connect-chat"; // id to prevent duplicate script
      script.src = "/assets/vendor/amazon-connect.js?v=" + hash;
      script.onload = () => {
        chatLoaded = true;
      };
      document.body.appendChild(script);
    })
    .catch((err) => {
      console.error("Failed to load Amazon Connect config, widget disabled:", err);
      // widget doesn't load on failure (no fallback)
    });
};
/** Removes support chat script */
const removeAmazonConnectChat = () => {
  if (process.env.SUPPORT_CHAT_ENABLED !== "true") return;
  if (!chatAdded || !chatLoaded) return;
  // remove script from document
  const script = document.getElementById("amazon-connect-chat");
  if (script) {
    script.remove();
  }

  const widget = document.getElementById("amazon-connect-chat-widget");
  if (widget) {
    widget.remove();
  }

  chatAdded = false;
};

const TRAINING_DUE_ALLOWED_PATHS = ["/", "/kbase"];
const Authenticated = () => {
  // navigate to dashboard when attempting to load login page while logged in
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useGlobalDispatch();

  const dueTraining = useSelector(selectDueTraining);

  useEffect(() => {
    if (dueTraining && !TRAINING_DUE_ALLOWED_PATHS.includes(location.pathname)) {
      navigate("/");
      dispatch(handleTrainingDue());
    }
  }, [dueTraining, location]);

  useEffect(() => {
    if (dueTraining) {
      sessionStorage.removeItem("_redirect");
      return;
    }
    // check for post login redirect
    const redirect = sessionStorage.getItem("_redirect");
    if (redirect) {
      sessionStorage.removeItem("_redirect");
      navigate(redirect);
    } else {
      if (location.pathname === "/login") {
        navigate("/");
      }
    }
  }, [location, dueTraining]);

  useEffect(() => {
    registerApps();
    addAmazonConnectChat();

    return removeAmazonConnectChat;
  }, []);

  useLoadFeatures(); // load features

  const apps = ["check-in", "repair", "check-out", "admin", "boh", "incident-advisor"];
  const isReleaseNotesPage = location.pathname === "/release-notes";

  return (
    <ConnectedPage>
      {apps.map((app) => (
        <div key={app} id={`single-spa-application:@ubif/${app}`} />
      ))}
      {isReleaseNotesPage && <ReleaseNotesPage />}
    </ConnectedPage>
  );
};

const Unauthenticated = () => {
  const { t } = useTranslation("root-config");
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useGlobalDispatch();

  // handle sign in error
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get("error");

    // remove error query param
    if (error) {
      params.delete("error");
      navigate({ search: params.toString() });

      // map error to message
      let message = t("login.error.generic");
      switch (error.toUpperCase() as SignInErrorCode) {
        case SignInErrorCode.USER_NOT_FOUND:
          message = t("login.error.userNotFound");
          break;
        case SignInErrorCode.STORE_NOT_FOUND:
          message = t("login.error.storeNotFound");
          break;
        case SignInErrorCode.SESSION_ERROR:
          message = t("login.error.sessionError");
          break;
        case SignInErrorCode.CLIENT_ERROR:
          message = t("login.error.clientError");
          break;
        case SignInErrorCode.SSO_ERROR:
          message = t("login.error.ssoError");
          break;
        case SignInErrorCode.SERVER_ERROR:
          message = t("login.error.serverError");
      }

      // show error toast
      dispatch(showToast({ message, toastType: "error" }));
    }
  }, [location]);

  const handleLoginClick = () => {
    window.location.href = "/api/auth/signin";
  };

  // save the current location to redirect back after login
  useEffect(() => {
    if (location.pathname !== "/login") {
      sessionStorage.setItem("_redirect", location.pathname + location.search);
    }
  }, [location]);

  // Anonymize once when entering the unauthenticated view — not on every route change,
  // which would split FullStory sessions without a page reload.
  useEffect(() => {
    fullStory((FS) => FS("setIdentity", { anonymous: true }));
    useBugsnag((Bugsnag) => {
      Bugsnag.setUser(null);
      Bugsnag.clearMetadata("store");
    });
  }, []);

  return (
    <>
      <Navigate to="/login" replace={true} />
      <LoginPage handleLoginClick={handleLoginClick} />
    </>
  );
};

const CenteredLoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const ThemeWrapper = styled.div`
  width: 100%;
  height: 100%;

  background-color: var(--aui-surface);
  color: var(--aui-on-surface);
`;

export const MainLayout = () => {
  const { isLoading, isError, error, refetch } = useGetUserQuery();
  const expiry = useSelector(selectSessionExpiry);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const toasts = useSelector(selectToasts);
  const sbApiError = useSelector(selectSBApiError);
  const dispatch = useGlobalDispatch();
  const hideToast = (id: string) => dispatch(removeToast(id));
  const isDarkThemeEnabled = useTypedSelector(selectLayoutFeature(LayoutFeature.theme_dark_enabled));
  const themeClassName = useAUIThemeClassName();
  const usedThemeClassName = isDarkThemeEnabled ? themeClassName : undefined;

  /**
   * Mirror the active theme class onto document.body so that portaled elements
   * (modals, dialogs, tooltips) rendered via createPortal outside of ThemeWrapper
   * still inherit the correct AUI CSS token values. Without this, portaled elements
   * always resolve to light-mode tokens regardless of the user's selected theme.
   */
  useEffect(() => {
    const { classList } = document.body;
    classList.remove("asurion-dark", "asurion-light");
    if (usedThemeClassName) classList.add(usedThemeClassName);
  }, [usedThemeClassName]);

  useEffect(once(initBugsnag), []);
  useEffect(() => {
    /**
     * reload user data 10 seconds post expiry to update expiry value
     */
    let timeout: ReturnType<typeof setTimeout>;
    if (expiry) timeout = setTimeout(() => void refetch(), expiry - Date.now() + 10000);
    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [expiry]);

  useEffect(() => {
    if (isError && error && typeof error === "object" && "status" in error) {
      const status = error.status;
      if (status !== 401) {
        dispatch(
          showToast({
            message: "An unexpected error occurred. Please try again.",
            toastType: "error",
            time: 3000
          })
        );
      }
    }
  }, [isError, error, dispatch]);

  if (isLoading)
    return (
      <ThemeWrapper className={usedThemeClassName}>
        <CenteredLoaderWrapper>
          <PageLoader data-testing-id="loading-spinner" />
        </CenteredLoaderWrapper>
      </ThemeWrapper>
    );

  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <ThemeWrapper className={usedThemeClassName}>
        {isError || !isAuthenticated ? <Unauthenticated /> : <Authenticated />}
        <UserPinModal />
        {toasts.map(({ actionUrl, ...toast }) => (
          <Toast
            isOpen
            key={toast.id}
            onCloseClick={() => hideToast(toast.id)}
            {...toast}
            onActionButtonClick={actionUrl ? () => window.open(actionUrl, "_blank") : toast.onActionButtonClick}
          />
        ))}
        {sbApiError.map((modal) => (
          <SBApiErrorsModal key={modal.id} {...modal} />
        ))}
      </ThemeWrapper>
    </StyleSheetManager>
  );
};
